import API from '../api/api'
import {$, ElementDecorator, ps, ViewTranslator} from '../util/view'
import {cast as castArray} from '../util/array'
import modal from './modal'
import tabs from './tabs'
import form from './form'
import confirmation from './confirmation'
import {NecessaryVendorList, notRegisteredVendors, Vendor, VendorList } from '../service/vendor-list'
import {PublisherPurposesList} from '../service/publisher-purposes-list'
import {REQUIRES_FORMS} from '../util/browser'
import {postTrackingEvent} from '../util/tracking'
import {I18n, Translator} from '../i18n'

export interface Options {
  api: API
  baseURL: string
  directVendorList: VendorList
  onAccept: EventListener | EventListener[]
  onCloseSettings: EventListener | EventListener[]
  onDecline: EventListener | EventListener[]
  necessaryVendorList: NecessaryVendorList
  notRegisteredVendors: notRegisteredVendors,
  t: ViewTranslator,
  translate: Translator,
  vendorList: VendorList
  vendors: Vendor[]
}

export interface SettingsForm {
  vendors: {[key: number]: boolean}
}

const accordion = (title: any, content: any) => {
  const children = [
    $('div', { class: 'mol-ads-cmp-accordion-title'}, [
      $('div', { class: 'mol-ads-cmp-accordion-title-container'}, [title]),
      $('div', { class: 'mol-ads-cmp-accordion-chevron'}, '❮')
    ]),
    $('div', { class: 'mol-ads-cmp-accordion-panel'}, content),
  ]
  return $('div', { class: 'mol-ads-cmp-accordion'}, children).click(e => {
    const target: any = e.currentTarget
    if (target) {
      const classNames = target.className.split(' ')
      if(classNames.find((el: any) => el === 'active')) classNames.pop()
      else classNames.push('active')
      target.className = classNames.join(' ')
      const panel = target.querySelector('.mol-ads-cmp-accordion-panel')
      panel.style.maxHeight = panel.scrollHeight + 'px'
    }
  })
}

const byName = (a: {name: string}, b: {name: string}): number => a.name.localeCompare(b.name)

const renderAdvertisers = (vendorList: any): ElementDecorator => {
  const { vendors, purposes, features } = vendorList
  return $('ul', {class: 'mol-ads-cmp--settings-consents'}, [...vendors].sort(byName).map(vendor =>
    $('li', {class: 'mol-ads-cmp--settings-consent'}, [
      accordion(
        $('div', {class: 'mol-ads-cmp-accordion-title-content'}, [
          $('div', {class: 'mol-ads-cmp--settings-consent-name' }, vendor.name),
          $('a', { class: 'mol-ads-cmp--settings-consent-policy', href: vendor.policyUrl, target: '_blank' }, 'Privacy policy')
        ]), [
          $('div', {}, Object.keys(vendor).filter(key => {
              const include = ['purposeIds', 'legIntPurposeIds', 'featureIds']
              if(include.find(el => (el === key && vendor[key].length > 0))) return true
              return false
            }).map(key => {
              const details: any = {
                purposeIds: {
                  title: 'Relying on Consent for:',
                  data: purposes,
                },
                legIntPurposeIds: {
                  title: 'Relying on Legitimate Interest for:',
                  data: purposes,
                },
                featureIds: {
                  title: 'Features:',
                  data: features
                }
              }
              return $('div', { class: 'mol-ads-cmp-list'}, [
                $('div', { class: 'mol-ads-cmp-list-header' }, details[key].title),
                $('ul', {}, vendor[key].map((id: any) => $('li', {}, `- ${details[key].data.find((el: any) => el.id === id).name}`)))
              ])
            })
          )
        ]),
    ])
  ))
}

export default function render ({
  api,
  baseURL,
  onAccept,
  onCloseSettings,
  onDecline,
  necessaryVendorList,
  notRegisteredVendors,
  t,
  translate,
  vendorList,
  vendors
}: Options): ElementDecorator {
  let selectedTab = 'functional'
  const settingTabs = tabs({
    items: [{
      body: (
        $('div', {}, [
          $('div', { class: 'mol-ads-cmp--settings-purposes-title' }, 'Purposes'),
          $('br'),
          'Associated Newspapers Limited participates in the ',
          $('a', {href: 'https://advertisingconsent.eu/', target: '_blank'}, 'IAB Europe Transparency & Consent Framework'),
          ' (IAB TCF) and complies with its specifications and policies. This Consent Management Platform is operated by us and has identification number ',
          $('a', {href: 'https://advertisingconsent.eu/cmp-list/', target: '_blank'}, '27'),
          '.',
          $('br'),
          $('br'),
          translate('We work with advertising partners to show content and advertisement for products and services you might like. Understand how your data may be used and who our partners are below:'),
          $('ul', {class: 'mol-ads-cmp--settings-consents'}, vendorList.purposes.map(({id, name, description }, i) =>
            $('li', {class: 'mol-ads-cmp--settings-consent'}, [
            accordion(
              $('div', {class: 'mol-ads-cmp-accordion-title-content'}, [
                $('div', {class: 'mol-ads-cmp--settings-consent-bullet' }, [
                  $('div', { class: 'mol-ads-cmp-circle' }, `${i+1}`),
                  $('div', {}, name)
                ])
              ]),
              $('div', {}, [
                $('div', {}, description),
                $('br'),
                $('div', {}, 'Vendors that use data in this way:'),
                $('br'),
                $('div', { class: 'mol-ads-policy-link-list' }, vendorList.vendors.filter(vendor => vendor.purposeIds.find(el => el  === id)).map(({ name, policyUrl }) =>
                 $('a', { href: policyUrl, target: '_blank' }, name)
                ))
              ])
            )
          ]))),
          $('h3', {}, translate('settings.advertising.purposes.descriptionExtraH5')),
          ...ps(translate('settings.advertising.purposes.descriptionExtraP5')),
          $('ul', {class: 'mol-ads-cmp--settings-consents'}, vendorList.features.map(({id, name }, i) =>
            $('li', {class: 'mol-ads-cmp--settings-consent'}, [
            accordion(
              $('div', {class: 'mol-ads-cmp-accordion-title-content'}, [
                $('div', {class: 'mol-ads-cmp--settings-consent-name' }, [
                  'A. Offline Data Matching',
                  'B. Device linking',
                  'C. Precise geographic location data'][i]),
              ]),
              $('div', {}, [
                $('div', {}, translate(`settings.advertising.purposes.descriptionExtraP${i+6}`)),
                $('br'),
                $('div', {}, 'Vendors that use data in this way:'),
                $('br'),
                $('div', { class: 'mol-ads-policy-link-list' }, vendorList.vendors.filter(vendor => vendor.featureIds.find(el => el  === id)).map(({ name, policyUrl }) =>
                 $('a', { href: policyUrl, target: '_blank' }, name)
                ))
              ])
            )
          ]))),
        ])
      ),
      onSelect: () => {
        selectedTab = 'advertising'
        postTrackingEvent('settings advertising tab clicked')
      },
      title: 'Purposes'
    }, {
      body: (
        $('div', {}, [
          $('strong', { class: 'mol-ads-tcf-vendors'}, 'Associated Newspapers Ltd.'),
          renderAdvertisers({
            purposes: vendorList.purposes,
            features: vendorList.features,
            vendors: [
              {
                id: 2,
                name: "Associated Newspapers Ltd. (MailOnline, ThisIsMoney & Metro)",
                policyUrl: "/privacy",
                purposeIds: [1, 2, 3, 4],
                legIntPurposeIds: [5],
                featureIds: [1]
              }
            ]
          }),
          $('strong', { class: 'mol-ads-tcf-vendors'}, 'TCF-Registered Vendors'),
          $('p', {class: 'mol-ads-vendors-paragraph'}, [
            'Below is a list of the third-party advertising companies who may have access to this data for the purposes and features they each declare when you click ',
            $('strong', {}, '"Got It" '),
            'or ',
            $('strong', {}, '"Allow All".')
          ]),
          renderAdvertisers(vendorList),
          $('strong', { class: 'mol-ads-tcf-vendors'}, 'Non-TCF Registered Vendors'),
          $('ul', {class: 'mol-ads-non-tcf-vendors'}, notRegisteredVendors.vendors.concat([
            {
              "id": 436,
              "name": "Akamai",
              "policyUrl": "https://www.akamai.com/uk/en/privacy-policies/",
              "description": "Akamai is a Content Delivery Network (CDN) which caches our site to provide our content as close to the readers device as possible, this provides fast response times and allows us to provide relevant content based on device and location"
            },
            {
              "id": 437,
              "name": "Associated Newspapers Ltd. (MailOnline, ThisIsMoney & Metro)",
              "policyUrl": "/privacy",
              "description": "Used to manage our in-house tracking and analytics. Used for instance, to keep control of your cookie preference settings"
            },
            {
              "id": 438,
              "name": "Amazon Web Services, Inc (“AWS”)",
              "policyUrl": "https://aws.amazon.com/privacy/?nc1=f_pr",
              "description": "AWS is a Content Delivery Network (CDN) which caches our site to provide our content as close to the readers device as possible, this provides fast response times and allows us to provide relevant content based on device and location"
            },
            {
              "id": 439,
              "name": "Adobe (Omniture)",
              "policyUrl": "https://www.adobe.com/uk/privacy.html",
              "description": "Omniture provides website analytics which allow us to ensure that our service is available for all readers. Omniture helps us understand what content is relevant to our readers"
            },
            {
              "id": 440,
              "name": "Crashlytics",
              "policyUrl": "https://try.crashlytics.com/terms/privacy-policy.pdf",
              "description": "Crashlytics provides us insight into why our native apps crash on your device, this allows us to refine the app to improve the experience"
            },
            {
              "id": 441,
              "name": "Fabric",
              "policyUrl": "https://fabric.io/terms?locale=en-us&utm_campaign=fabric-marketing&utm_medium=natural",
              "description": "Fabric provides us insight into why our native apps crash on your device, this allows us to refine the app to improve the experience"
            },
            {
              "id": 442,
              "name": "Hockey app",
              "policyUrl": "https://go.microsoft.com/fwlink/?linkid=248681&clcid=0x409",
              "description": "Hockey App provides us insight into why our native apps crash on your device, this allows us to refine the app to improve the experience"
            }
          ]).sort((a: any, b: any) => 
            (a.name.toLowerCase() < b.name.toLowerCase()) ? -1 : (a.name.toLowerCase() > b.name.toLowerCase()) ? 1 : 0
          ).map((vendor: any) =>
            $('li', {}, [
              $('div', {class: 'mol-ads-non-tcf-item'}, [
                $('div', {}, vendor.name),
                $('a', { href: vendor.policyUrl, target: '_blank' }, 'Privacy policy')
              ])
            ]))
          )
        ])
      ),
      onSelect: () => {
        selectedTab = 'functional'
        postTrackingEvent('settings functional tab clicked')
      },
      title: 'Vendors'
    }, ],
    onSwitch: () => {
      // settings.raw.classList.toggle('mol-ads-cmp--settings-functional')
      // settings.raw.classList.toggle('mol-ads-cmp--settings-advertising')
    }
  })

  const confirm = () => {
    const close = () => {
      view.close()
    }

    const view = confirmation({
      confirmText: translate('settings.confirmation.confirm'),
      declineText: translate('settings.confirmation.decline'),
      footer: REQUIRES_FORMS ? [
        form({
          attributes: {style: 'display:inline;'},
          baseURL,
          children: $('button', {
            class: 'mol-ads-cmp--btn-secondary',
            type: 'submit'
          }, 'I\'m sure'),
          consent: false
        }),
        form({
          attributes: {style: 'display:inline;'},
          baseURL,
          children: $('button', {
            class: 'mol-ads-cmp--btn-primary',
            type: 'submit'
          }, 'Ok, allow all'),
          consent: true
        })
      ] : undefined,
      message: translate('settings.confirmation.description'),
      onConfirm: [
        close,
        () => postTrackingEvent('settings advertising panel disallow declined'),
        ...castArray(onAccept),
        ...castArray(onCloseSettings)
      ],
      onDecline: [
        close,
        () => postTrackingEvent('settings advertising panel disallow confirmed'),
        ...castArray(onDecline),
        ...castArray(onCloseSettings)
      ],
      title: translate('settings.confirmation.title')
    })

    settings.appendChild(view)
  }

  const settingsModal = (
    modal({
      body: settingTabs,
      footer: [
        $('a', { class: 'mol-ads-cmp-footer-privacy', href: 'https://www.dailymail.co.uk/privacy', target: '_blank' }, 'Privacy & Cookies Policy'),
        $('div', { class: 'mol-ads-cmp-footer-buttons' }, [
          $('button', {class: 'mol-ads-cmp--btn-secondary', type: 'button'}, 'Don\'t allow')
          .click(confirm)
          .click(() => postTrackingEvent('settings advertising panel disallow')),
          $('button', {class: 'mol-ads-cmp--btn-primary', type: 'button'}, 'Allow all')
          .click(() => postTrackingEvent(`settings ${selectedTab} panel allow all`))
          .click(onAccept)
          .click(onCloseSettings)
        ])
      ],
      title: translate('settings.title')
    })
  )

  const settings =
    $('div', {class: 'mol-ads-cmp--settings mol-ads-cmp--settings-advertising'}, settingsModal)

  return settings
}
